import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './AdminDialog';
import { Copy, Check, ExternalLink } from 'lucide-react';

/**
 * DetailsItem - Key-value pair with optional copy button and badge
 */
export function DetailsItem({
  label,
  value,
  copyable = false,
  badge = false,
  badgeColor = 'default',
  link,
  xs = 12,
  sm = 6,
  md = 4,
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderValue = () => {
    if (value === null || value === undefined || value === '') {
      return (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          Not provided
        </Typography>
      );
    }

    if (badge) {
      return (
        <Chip
          size="small"
          label={String(value)}
          color={badgeColor}
          sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }}
        />
      );
    }

    if (link) {
      return (
        <Box
          component="a"
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'primary.main',
            textDecoration: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          {String(value)}
          <ExternalLink size={13} />
        </Box>
      );
    }

    return (
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
          wordBreak: 'break-word',
          fontSize: '0.875rem',
        }}
      >
        {String(value)}
      </Typography>
    );
  };

  return (
    <Grid size={{ xs, sm, md }}>
      <Box sx={{ mb: 1 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            fontSize: '0.7rem',
            mb: 0.5,
          }}
        >
          {label}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {renderValue()}
          {copyable && value && (
            <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  p: 0.25,
                  color: copied ? 'success.main' : 'text.disabled',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Grid>
  );
}

/**
 * DetailsSection - Visual grouping card for entity properties
 */
export function DetailsSection({ title, subtitle, action, children, sx = {} }) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        mb: 2.5,
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.4) : 'background.paper',
        ...sx,
      }}
    >
      {(title || subtitle || action) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
            pb: 1.25,
            borderBottom: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Box>
            {title && (
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {action}
        </Box>
      )}

      <Grid container spacing={2}>
        {children}
      </Grid>
    </Paper>
  );
}

/**
 * DetailsDialog - High-level modal for inspecting entities (products, users, businesses, enquiries, RFQs)
 */
export default function DetailsDialog({
  open,
  onClose,
  title,
  subtitle,
  icon,
  iconColor = 'primary',
  badge,
  badgeColor,
  maxWidth = 'md',
  fullWidth = true,
  fullScreenOnMobile = true,
  editAction,
  deleteAction,
  extraActions,
  children,
  sx = {},
}) {
  return (
    <AdminDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreenOnMobile={fullScreenOnMobile}
      scroll="paper"
      sx={sx}
    >
      <AdminDialogHeader
        title={title}
        subtitle={subtitle}
        icon={icon}
        iconColor={iconColor}
        badge={badge}
        badgeColor={badgeColor}
        onClose={onClose}
      />

      <AdminDialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        {children}
      </AdminDialogContent>

      <AdminDialogActions>
        {deleteAction}
        <Box sx={{ flexGrow: 1 }} />
        {extraActions}
        <Button variant="outlined" onClick={onClose} sx={{ minWidth: 84, fontWeight: 600 }}>
          Close
        </Button>
        {editAction}
      </AdminDialogActions>
    </AdminDialog>
  );
}

DetailsDialog.Section = DetailsSection;
DetailsDialog.Item = DetailsItem;
